import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import type { Shipment } from "@/lib/types";
import { Alert, Button, Card, Spinner } from "@/components/ui";
import { LogoMark } from "@/components/Logo";
import { SiteFooter } from "@/components/SiteFooter";

interface GuestCheckoutStatus {
  status: "pending" | "booked" | "failed";
  shipment?: Shipment;
  failure_reason?: string;
}

/**
 * Where a guest lands after paying.
 *
 * The gateway redirect here is not proof the money arrived -- it never is,
 * anywhere on this platform -- so this polls the same status the webhook
 * writes once it actually settles the payment and books the shipment.
 */
export function GuestStatus() {
  const { paymentId = "" } = useParams();

  const { data, isLoading } = useQuery({
    queryKey: ["guest-checkout", paymentId],
    queryFn: () => api.public<GuestCheckoutStatus>(`/v1/guest/shipments/status/${encodeURIComponent(paymentId)}`),
    enabled: paymentId.length > 0,
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2500 : false),
  });

  return (
    <div className="min-h-dvh bg-ink-100">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <LogoMark className="h-7 w-7" />
            WeShip
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-14">
        {(isLoading || data?.status === "pending") && (
          <Card className="flex flex-col items-center gap-4 py-14 text-center">
            <Spinner className="h-6 w-6" />
            <div>
              <p className="text-lg font-semibold">Confirming your payment</p>
              <p className="mt-1 text-sm text-ink-500">
                This usually takes a few seconds. Do not close this page.
              </p>
            </div>
          </Card>
        )}

        {data?.status === "failed" && (
          <Card className="py-10 text-center">
            <p className="text-lg font-semibold">We could not complete this booking</p>
            <div className="mx-auto mt-4 max-w-md">
              <Alert tone="warn">
                {data.failure_reason ?? "The payment could not be completed. No shipment was booked."}
              </Alert>
            </div>
            <p className="mt-4 text-sm text-ink-500">
              If your card was charged, it has been refunded automatically.
            </p>
            <Button className="mt-6" onClick={() => (window.location.href = "/ship")}>
              Try again
            </Button>
          </Card>
        )}

        {data?.status === "booked" && data.shipment && (
          <Card className="py-10 text-center">
            <p className="text-3xl">✓</p>
            <p className="mt-2 text-xl font-semibold tracking-tight">Booked</p>
            <p className="mt-1 text-sm text-ink-500">
              Your collection is on the way. A receipt and this tracking link were sent to your email.
            </p>

            <div className="mx-auto mt-6 max-w-sm rounded-xl border border-ink-200 p-5 text-left">
              <p className="text-xs uppercase tracking-wide text-ink-400">Waybill</p>
              <p className="font-mono text-lg">{data.shipment.waybill}</p>
              <p className="mt-3 text-xs uppercase tracking-wide text-ink-400">Paid</p>
              <p className="text-lg font-semibold">{money(data.shipment.price_cents)}</p>
            </div>

            <Button className="mt-6" onClick={() => (window.location.href = `/track/${data.shipment!.waybill}`)}>
              Track this shipment
            </Button>
          </Card>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
